import { createContext, useContext } from 'react';
import type { Clock } from '../lib/clock';
import type { ObservationTarget } from '../host/observation';
import type { MeResult, WorkspaceResult } from '../lib/queries';

/**
 * Les deux contextes du bureau : ce qui est vrai **pour toute la session** (qui est connecté,
 * quelle classe est affichée, l'horloge) et ce qui est vrai **pour une fenêtre** (est-elle devant,
 * est-elle réduite, que font ses trois pastilles).
 *
 * Ils remplacent le contexte d'`Outlet` de React Router, qui ne pouvait plus servir : depuis le
 * passage au multi-fenêtres, plusieurs fenêtres sont montées **en même temps** et chacune joue son
 * propre jeu de routes. Il n'y a donc plus un `Outlet` unique dont on hérite, mais une pile — et
 * ce qui distingue deux fenêtres montées côte à côte ne peut pas venir d'un contexte partagé.
 */

/**
 * Les écrans de l'espace enseignant. Déclarée **ici** et non dans `teacher/TeacherSpace.tsx`
 * parce que le bureau doit pouvoir en désigner un depuis n'importe où : quand un écran d'élève dit
 * à l'enseignant ce qu'il peut faire, le geste doit être à portée de clic (« Distribuer un fichier
 * à cette classe », « Lancer une activité »), et le bouton qui l'ouvre vit dans `host/AppHost.tsx`
 * ou dans `activity/ActivityBoard.tsx`, pas dans l'espace enseignant.
 */
export type TeacherSection =
  | 'presence'
  | 'journal'
  | 'activities'
  | 'datasets'
  | 'apps'
  | 'environments'
  | 'practice'
  | 'class'
  | 'budget'
  /** Les documents que l'enseignant fait passer à sa classe : déposer, lister, retirer (voir
   * `teacher/DocumentsPanel.tsx`). L'autre bout est l'entrée « Documents » du dock, celle que
   * l'élève ouvre. */
  | 'documents'
  /** Prévenir la classe : le message qui allume la cloche de l'élève (voir
   * `teacher/NotifyPanel.tsx`). */
  | 'notify'
  /** L'année scolaire et les enseignants : ce qui vit AU-DESSUS d'une classe, donc hors du
   * sélecteur de classe du rail (voir `teacher/SchoolPanel.tsx`). */
  | 'school';

export interface DeskContextValue {
  me: MeResult;
  classId: string | undefined;
  className: string | undefined;
  workspace: WorkspaceResult | null | undefined;
  /** L'horloge du bureau, passée aux fenêtres plutôt que reconstruite par chacune : le bureau n'a
   * qu'un seul `new Date()` (`lib/clock.ts`), et une page qui en fabriquerait une seconde pourrait
   * dériver de la barre de menu sans que rien ne le signale. */
  clock: Clock;
  /** Ouvre une app — **le même geste que l'icône du dock**, jamais une seconde navigation écrite
   * à côté. Posé sur le contexte parce que la consigne du jour (`activity/ActivityBoard.tsx`)
   * porte un bouton « ouvrir l'outil » : sans lui, cet écran aurait dû recopier la résolution de
   * la route par défaut du manifeste et la logique de pile, et les deux auraient fini par diverger
   * (une app déjà ouverte se serait dédoublée d'un côté et pas de l'autre). L'`appId` reste opaque
   * pour le bureau, qui ne le compare jamais à une valeur écrite en dur. */
  openApp: (appId: string, route?: string) => void;
  /**
   * Ouvre l'espace enseignant **sur une section précise** — le même geste que son icône du dock,
   * jamais une seconde navigation écrite à côté (voir `openApp` ci-dessus, pour la même raison).
   *
   * Ce qui l'a rendu nécessaire : un écran d'élève ouvert par l'enseignant ne doit pas se contenter
   * de lui dire ce qui manque, il doit lui donner le geste. « Vous n'avez pas encore distribué de
   * fichier client à cette classe » sans bouton, c'est encore une phrase qui demande d'aller
   * chercher ailleurs.
   *
   * Réservé aux enseignants côté bouton, mais ce n'est pas ce bouton qui protège : chaque opération
   * de cet espace refuse un élève côté serveur (`role: 'teacher'`).
   */
  openTeacher: (section: TeacherSection) => void;
  /**
   * **De quel élève cet onglet lit le travail** — `null` le reste du temps, c'est-à-dire toujours
   * pour un élève.
   *
   * Quand ce champ n'est pas `null`, `workspace` ci-dessus est celui de CET élève, et non celui de
   * l'acteur : toutes les fenêtres du bureau sont donc montées sur son espace, en lecture seule.
   * Le refus d'écrire ne vient PAS de ce champ — il vient du serveur (transaction ouverte
   * `read only`, opérations d'app refusées si elles ne sont pas des lectures) ; ce que le bureau
   * en fait, c'est le DIRE : un bandeau permanent, et des gestes d'écriture qui ne prétendent pas
   * fonctionner.
   */
  observing: ObservationTarget | null;
  /** Revenir à son propre espace. Porté par le bandeau, visible en permanence — plein écran
   * compris. */
  stopObserving: () => void;
}

const DeskCtx = createContext<DeskContextValue | null>(null);
export const DeskProvider = DeskCtx.Provider;

export function useDesk(): DeskContextValue {
  const value = useContext(DeskCtx);
  if (value === null) {
    throw new Error("useDesk() a été appelé hors du bureau : aucun DeskProvider n'est monté.");
  }
  return value;
}

/**
 * Ce qu'une fenêtre sait d'elle-même. `AppWindow` le lit ; les pages hébergées (`AppHost`,
 * `ProfilePage`, `TeacherSpace`) n'ont plus à le transporter.
 */
export interface WindowFrame {
  storageKey: string;
  /** Au premier plan. C'est la fenêtre que désigne l'adresse — voir `stack.ts`. */
  active: boolean;
  /** Plein écran. **Seule la fenêtre active peut l'être** : le plein écran est un attribut du
   * document, et l'attribuer à une fenêtre du fond n'aurait aucun sens. */
  fullscreen: boolean;
  /**
   * Le **bureau** est en plein écran — cette fenêtre-ci, ou une autre.
   *
   * La distinction n'est pas cosmétique : pendant la projection, `.desk` perd ses marges
   * (`desk.css`, `body[data-fs] .desk { padding: 0 }`), donc la place naturelle de TOUTES les
   * fenêtres se déplace, y compris celles du fond qui sont seulement masquées. `useWindowDrag` s'en
   * sert pour ne pas borner une fenêtre contre une géométrie qui n'existera plus à la sortie —
   * sans quoi chaque projection remontait les fenêtres du fond de 20 pixels.
   */
  deskFullscreen: boolean;
  minimized: boolean;
  /** Rang d'empilement CSS, distribué par le bureau à partir de l'ordre de la pile. */
  zIndex: number;
  /** La place de la fenêtre dans la cascade d'ouverture (`DeskWindow.cascade`, `desk/stack.ts`).
   * `AppWindow` la donne à `useWindowDrag`, qui en tire un décalage initial borné : sans elle,
   * toutes les fenêtres s'ouvraient exactement l'une sur l'autre. */
  cascade: number;
  /** Ramène la fenêtre au premier plan. Posé sur toute la fenêtre : cliquer n'importe où dedans
   * la fait passer devant, comme dans un vrai système. */
  focus: () => void;
  close: () => void;
  minimize: () => void;
  toggleFullscreen: () => void;
}

const WindowCtx = createContext<WindowFrame | null>(null);
export const WindowProvider = WindowCtx.Provider;

export function useWindowFrame(): WindowFrame {
  const value = useContext(WindowCtx);
  if (value === null) {
    throw new Error(
      "useWindowFrame() a été appelé hors d'une fenêtre : aucun WindowProvider n'est monté.",
    );
  }
  return value;
}
